"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowLeft, ArrowRight, GraduationCap } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MobileMenuButton } from "@/components/ui/mobile-menu-button";
import { CourseProgressGrid } from "@/components/course/CourseProgressGrid";
import { CourseHeaderProgress } from "@/components/course/CourseHeaderProgress";
import { useI18n } from "@/lib/i18n";

type Idiom = { id: number; title: string };

interface CourseOverviewClientProps {
  slug: string;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  firstId?: number;
  items: Idiom[];
  error?: string;
  title: string;
}

export function CourseOverviewClient({
  slug,
  page,
  pageSize,
  total,
  totalPages,
  firstId,
  items,
  error,
  title,
}: CourseOverviewClientProps) {
  const { t } = useI18n();

  const courseSlugKey = slug === "most-common-american-idioms" ? "idioms" : "default";
  const courseTitle = courseSlugKey === "idioms" ? t("course.detail.courses.idioms.title") : title;
  const courseTagline = courseSlugKey === "idioms" ? t("course.detail.courses.idioms.tagline") : "";
  const courseProvider = courseSlugKey === "idioms" ? t("course.detail.courses.idioms.provider") : "";
  const courseCoverAlt = courseSlugKey === "idioms" ? t("course.detail.courses.idioms.coverAlt") : `${title} cover`;

  const paginationLabel = t("course.detail.pagination")
    .replace("{page}", String(page))
    .replace("{totalPages}", String(totalPages))
    .replace("{total}", String(total))
    .replace("{unit}", t("course.detail.idiomUnit"));

  const totalBadgeLabel = t("course.detail.totalBadge")
    .replace("{total}", String(total))
    .replace("{unit}", t("course.detail.idiomUnit"));

  return (
    <div className="container mx-auto px-4 py-8">
      <MobileMenuButton ariaLabel={t("course.detail.menuButton", "Open menu")} />
      <div className="mb-6 space-y-2">
        <div className="flex items-center gap-3">
          <GraduationCap className="w-8 h-8" />
          <h1 className="text-2xl sm:text-3xl font-bold">{t("course.detail.headerTitle")}</h1>
        </div>
        <div className="text-sm text-muted-foreground mb-2">
          <Link href="/course" className="hover:underline">
            {t("course.detail.breadcrumb.courses")}
          </Link>
          <span className="mx-2">/</span>
          <span>{courseTitle}</span>
        </div>
      </div>

      <Card className="mb-6 border-0 shadow-none">
        <CardContent className="p-0">
          <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-5 items-center">
            <div className="relative rounded-xl border overflow-hidden aspect-[3/4]">
              <Image
                src="/images/cover.png"
                alt={courseCoverAlt}
                fill
                className="object-cover"
                priority
                sizes="(max-width: 640px) 100vw, 220px"
              />
            </div>
            <div>
              {courseProvider ? (
                <div className="text-sm text-muted-foreground mb-1">{courseProvider}</div>
              ) : null}
              <h1 className="text-2xl font-semibold mb-1">{courseTitle}</h1>
              {courseTagline ? <p className="text-sm text-muted-foreground mb-3">{courseTagline}</p> : null}
              <div className="flex items-center gap-3 flex-wrap">
                <Button asChild>
                  <Link href={`/course/${slug}/${firstId ?? items[0]?.id ?? 1}`}>
                    {t("course.detail.actions.continue")}
                  </Link>
                </Button>
                <Badge variant="secondary">{totalBadgeLabel}</Badge>
                <CourseHeaderProgress slug={slug} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 mb-6">
          {t("course.detail.errors.load").replace("{message}", error)}
        </div>
      )}

      {!error && (
        <div className="text-sm text-muted-foreground mb-4">{paginationLabel}</div>
      )}

      <CourseProgressGrid slug={slug} items={items as any} />

      <div className="mt-6 flex items-center justify-center gap-3 pb-12">
        <Button asChild variant="outline" size="sm" disabled={page <= 1}>
          <Link href={`/course/${slug}?page=${Math.max(1, page - 1)}&pageSize=${pageSize}`}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t("course.detail.paginationPrev")}
          </Link>
        </Button>
        <span className="text-sm text-muted-foreground">
          {page} / {totalPages}
        </span>
        <Button asChild variant="outline" size="sm" disabled={page >= totalPages}>
          <Link href={`/course/${slug}?page=${Math.min(totalPages, page + 1)}&pageSize=${pageSize}`}>
            {t("course.detail.paginationNext")} <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
